import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, User, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Teacher {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  department?: string;
  position_title?: string;
  school_name?: string;
}

interface IntelligentTeacherSearchProps {
  onSelectTeacher: (teacherId: string) => void;
  selectedTeacherId?: string;
}

export function IntelligentTeacherSearch({ onSelectTeacher, selectedTeacherId }: IntelligentTeacherSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Teacher[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (selectedTeacherId && !selectedTeacher) {
      loadSelectedTeacher(selectedTeacherId);
    }
  }, [selectedTeacherId]);

  const loadSelectedTeacher = async (teacherId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, department, position_title, school_name')
        .eq('id', teacherId)
        .single();

      if (error) throw error;
      if (data) setSelectedTeacher(data as Teacher);
    } catch (error) {
      console.error('Error loading selected teacher:', error);
    }
  };

  const searchTeachers = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: adminProfile } = await supabase
        .from('profiles')
        .select('district_id')
        .eq('id', user.id)
        .single();

      if (!adminProfile) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, department, position_title, school_name')
        .eq('district_id', adminProfile.district_id)
        .neq('is_admin', true)
        .or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%,email.ilike.%${query}%,department.ilike.%${query}%,position_title.ilike.%${query}%,school_name.ilike.%${query}%`)
        .order('last_name')
        .limit(20);

      if (error) throw error;
      setSearchResults(data || []);
      setShowResults(true);
    } catch (error) {
      console.error('Error searching teachers:', error);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      searchTeachers(searchQuery);
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleSelectTeacher = (teacher: Teacher) => {
    setSelectedTeacher(teacher);
    setSearchQuery('');
    setShowResults(false);
    onSelectTeacher(teacher.id);
  };

  const handleClearSelection = () => {
    setSelectedTeacher(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <div className="space-y-4">
      {selectedTeacher ? (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium">
                    {selectedTeacher.first_name} {selectedTeacher.last_name}
                  </h4>
                  <div className="flex items-center gap-2 mt-1">
                    {selectedTeacher.department && (
                      <Badge variant="secondary" className="text-xs">
                        {selectedTeacher.department}
                      </Badge>
                    )}
                    {selectedTeacher.position_title && (
                      <span className="text-xs text-muted-foreground">
                        {selectedTeacher.position_title}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={handleClearSelection}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, department, school..."
              className="pl-10"
              onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
            />
          </div>

          {showResults && searchResults.length > 0 && (
            <Card className="absolute w-full mt-2 z-50 max-h-96 overflow-y-auto">
              <CardContent className="p-2">
                <div className="space-y-1">
                  {searchResults.map((teacher) => (
                    <div
                      key={teacher.id}
                      className="p-3 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => handleSelectTeacher(teacher)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">
                            {teacher.first_name} {teacher.last_name}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {teacher.email}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {teacher.department && (
                              <Badge variant="secondary" className="text-xs">
                                {teacher.department}
                              </Badge>
                            )}
                            {teacher.school_name && (
                              <span className="text-xs text-muted-foreground">
                                {teacher.school_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {isSearching && (
            <div className="absolute w-full mt-2">
              <Card>
                <CardContent className="p-4 text-center text-muted-foreground text-sm">
                  Searching...
                </CardContent>
              </Card>
            </div>
          )}

          {showResults && searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
            <div className="absolute w-full mt-2">
              <Card>
                <CardContent className="p-4 text-center text-muted-foreground text-sm">
                  No teachers found matching "{searchQuery}"
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
